
import React, { useState, useEffect, useRef, DragEvent } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import * as XLSX from 'xlsx';

// Import our new components
import { LeaderboardItem, SupabaseLeaderboardItem } from "./leaderboard/types";
import ImportDialog from "./leaderboard/ImportDialog";
import ErrorDialog from "./leaderboard/ErrorDialog";
import EmptyState from "./leaderboard/EmptyState";
import ProgressDisplay from "./leaderboard/ProgressDisplay";
import LeaderboardFilters from "./leaderboard/LeaderboardFilters";
import LeaderboardCard from "./leaderboard/LeaderboardCard";
import { processImportedData, processFile, saveLeaderboardData } from "./leaderboard/excelUtils";
import RevenueDashboard from "./leaderboard/RevenueDashboard";

// Constants
const industries = ["All", "Telecom", "Technology", "FSI", "Manufacturing", "Public Sector", "Others"];
const regions = ["All", "North America", "Europe", "Asia Pacific", "Latin America", "Middle East & Africa"];
const productLines = ["All", "Enterprise Solutions", "Cloud Services", "Security Software", "Data Analytics", "AI & Machine Learning", "NA"];

// Styling for the progress swoosh effect
const swooshingStyle = `
  @keyframes swoosh {
    0% {
      opacity: 0;
      transform: translateX(-100%);
    }
    50% {
      opacity: 0.8;
    }
    100% {
      opacity: 0;
      transform: translateX(100%);
    }
  }
  
  .progress-swoosh::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent);
    animation: swoosh 3s ease-in-out infinite;
    border-radius: inherit;
    z-index: 10;
  }
`;

const LeaderboardTab = () => {
  // State management
  const [selectedRegion, setSelectedRegion] = useState<string>("All");
  const [selectedCountry, setSelectedCountry] = useState<string>("All");
  const [countryOptions, setCountryOptions] = useState<string[]>(["All"]);
  const [selectedAccountName, setSelectedAccountName] = useState<string>("All");
  const [accountNameOptions, setAccountNameOptions] = useState<string[]>(["All"]);
  const [selectedProductLine, setSelectedProductLine] = useState<string>("All");
  const [selectedIndustry, setSelectedIndustry] = useState<string>("All");
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardItem[]>([]);
  const [originalData, setOriginalData] = useState<LeaderboardItem[]>([]);
  const [totalAchievement, setTotalAchievement] = useState<number>(0);
  const [totalTarget, setTotalTarget] = useState<number>(0);
  const [totalPercentage, setTotalPercentage] = useState<number>(0);
  const [filterType, setFilterType] = useState<"product" | "industry">("product");
  const [isImportDialogOpen, setIsImportDialogOpen] = useState<boolean>(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isDialogDragging, setIsDialogDragging] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const dialogDropZoneRef = useRef<HTMLDivElement>(null);

  // Initial data load
  useEffect(() => {
    fetchLeaderboardData();
  }, []);

  // Update country options when region changes
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        let query = supabase.from('leaderboard').select('country');
        if (selectedRegion !== "All") {
          query = query.eq('region', selectedRegion);
        }
        const { data, error } = await query;
        
        if (error) {
          throw new Error(error.message);
        }
        
        const countries = data.map(item => item.country).filter(country => country !== null && country !== undefined && country !== "") as string[];
        const uniqueCountries = [...new Set(countries)];
        setCountryOptions(["All", ...uniqueCountries.sort()]);
        
        if (selectedCountry !== "All" && !uniqueCountries.includes(selectedCountry)) {
          setSelectedCountry("All");
        }

        setSelectedAccountName("All");
      } catch (err) {
        console.error("Error fetching country data:", err);
      }
    };
    
    fetchCountries();
  }, [selectedRegion]);

  // Update account options when region/country changes
  useEffect(() => {
    const fetchAccountNames = async () => {
      try {
        let query = supabase.from('leaderboard').select('account_name');
        
        if (selectedRegion !== "All") {
          query = query.eq('region', selectedRegion);
        }
        
        if (selectedCountry !== "All") {
          query = query.eq('country', selectedCountry);
        }
        
        const { data, error } = await query;
        
        if (error) {
          throw new Error(error.message);
        }
        
        const accountNames = data.map(item => item.account_name).filter(name => name !== null && name !== undefined && name !== "") as string[];
        const uniqueAccountNames = [...new Set(accountNames)];
        setAccountNameOptions(["All", ...uniqueAccountNames.sort()]);
        
        if (selectedAccountName !== "All" && !uniqueAccountNames.includes(selectedAccountName)) {
          setSelectedAccountName("All");
        }
      } catch (err) {
        console.error("Error fetching account name data:", err);
      }
    };
    
    fetchAccountNames();
  }, [selectedRegion, selectedCountry]);

  // Update filtered data when filters change
  useEffect(() => {
    let filteredData = [...originalData];
    
    if (selectedRegion !== "All") {
      filteredData = filteredData.filter(item => item.region === selectedRegion);
    }
    
    if (selectedCountry !== "All") {
      filteredData = filteredData.filter(item => item.country === selectedCountry);
    }

    if (selectedAccountName !== "All") {
      filteredData = filteredData.filter(item => item.accountName === selectedAccountName);
    }
    
    if (filterType === "product" && selectedProductLine !== "All") {
      filteredData = filteredData.filter(item => item.productLine === selectedProductLine);
    } else if (filterType === "industry" && selectedIndustry !== "All") {
      filteredData = filteredData.filter(item => item.industry === selectedIndustry);
    }
    
    // Calculate totals
    const sumAchievement = filteredData.reduce((sum, item) => sum + item.achievement, 0);
    const sumTarget = filteredData.reduce((sum, item) => sum + item.target, 0);
    const percentage = sumTarget > 0 ? sumAchievement / sumTarget * 100 : 0;
    
    setTotalAchievement(sumAchievement);
    setTotalTarget(sumTarget);
    setTotalPercentage(parseFloat(percentage.toFixed(1)));
    setLeaderboardData(filteredData);
  }, [selectedRegion, selectedCountry, selectedAccountName, selectedProductLine, selectedIndustry, originalData, filterType]);

  // Data fetching
  const fetchLeaderboardData = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("leaderboard")
        .select("*")
        .order("id", { ascending: true });
      
      if (error) {
        console.error("Error fetching leaderboard data:", error);
        setIsLoading(false);
        return;
      }
      
      if (data && data.length > 0) {
        const transformedData: LeaderboardItem[] = data.map((item: SupabaseLeaderboardItem) => {
          const percentage = item.target > 0 ? item.achievement / item.target * 100 : 0;
          return {
            id: String(item.id),
            name: item.name,
            region: item.region,
            productLine: item.product_line,
            industry: item.industry,
            target: item.target,
            achievement: item.achievement,
            cogs: item.cogs,
            percentage: parseFloat(percentage.toFixed(2)),
            forecast: item.forecast,
            y1Forecast: item.y_1_forecast,
            y2Forecast: item.y_2_forecast,
            y1Actuals: item.y_1_actuals,
            y2Actuals: item.y_2_actuals,
            accountName: item.account_name,
            country: item.country,
            revenueGap: item.revenue_gap,
            prospectingStageValue: item.prospecting_stage_value,
            qualificationStageValue: item.qualification_stage_value,
            proposalStageValue: item.proposal_stage_value,
            negotiationStageValue: item.negotiation_stage_value,
            lateStageTotal: item.late_stage_total_value,
            lateStageToGapRatio: item.late_stage_to_gap_ratio,
            historicalWinRatio: item.historical_win_ratio
          };
        });
        
        transformedData.sort((a, b) => (b.percentage || 0) - (a.percentage || 0));
        setOriginalData(transformedData);
      }
    } catch (err) {
      console.error("Unexpected error fetching leaderboard data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper methods for file upload handling
  const handleImportClick = () => {
    setIsImportDialogOpen(true);
  };

  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(
        file, 
        fileInputRef,
        setErrorMessage,
        setIsErrorDialogOpen,
        setIsImportDialogOpen,
        (jsonData) => processImportedData(jsonData, setErrorMessage, setIsErrorDialogOpen, setOriginalData)
      );
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      processFile(
        file, 
        fileInputRef,
        setErrorMessage,
        setIsErrorDialogOpen,
        setIsImportDialogOpen,
        (jsonData) => processImportedData(jsonData, setErrorMessage, setIsErrorDialogOpen, setOriginalData)
      );
    }
  };

  // Dialog drag and drop handlers
  const handleDialogDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDialogDragging(true);
  };

  const handleDialogDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDialogDragging) {
      setIsDialogDragging(true);
    }
  };

  const handleDialogDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDialogDragging(false);
  };

  const handleDialogDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDialogDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      processFile(
        file, 
        fileInputRef,
        setErrorMessage,
        setIsErrorDialogOpen,
        setIsImportDialogOpen,
        (jsonData) => processImportedData(jsonData, setErrorMessage, setIsErrorDialogOpen, setOriginalData)
      );
    }
  };

  // Helper method to get the filter summary text
  const getFilterSummary = () => {
    const parts = [];
    
    if (selectedRegion !== "All") {
      parts.push(`Region: ${selectedRegion}`);
    }
    
    if (selectedCountry !== "All") {
      parts.push(`Country: ${selectedCountry}`);
    }
    
    if (selectedAccountName !== "All") {
      parts.push(`Account: ${selectedAccountName}`);
    }
    
    if (filterType === "product" && selectedProductLine !== "All") {
      parts.push(`Product: ${selectedProductLine}`);
    } else if (filterType === "industry" && selectedIndustry !== "All") {
      parts.push(`Industry: ${selectedIndustry}`);
    }
    
    return parts.length > 0 ? `(${parts.join(", ")})` : "";
  };

  return (
    <div className="text-white">
      <style>{swooshingStyle}</style>
      
      {originalData.length === 0 && !isLoading ? (
        <EmptyState
          onFileSelect={handleFileSelect}
          dropZoneRef={dropZoneRef}
          isDragging={isDragging}
          handleDragEnter={handleDragEnter}
          handleDragOver={handleDragOver}
          handleDragLeave={handleDragLeave}
          handleDrop={handleDrop}
        />
      ) : (
        <>
          <LeaderboardFilters
            onImportClick={handleImportClick}
            filterType={filterType}
            setFilterType={setFilterType}
            selectedRegion={selectedRegion}
            setSelectedRegion={setSelectedRegion}
            regions={regions}
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountry}
            countryOptions={countryOptions}
            selectedAccountName={selectedAccountName}
            setSelectedAccountName={setSelectedAccountName}
            accountNameOptions={accountNameOptions}
            selectedProductLine={selectedProductLine}
            setSelectedProductLine={setSelectedProductLine}
            productLines={productLines}
            selectedIndustry={selectedIndustry}
            setSelectedIndustry={setSelectedIndustry}
            industries={industries}
          />
          
          <ProgressDisplay
            totalAchievement={totalAchievement}
            totalTarget={totalTarget}
            totalPercentage={totalPercentage}
            filterSummary={getFilterSummary()}
          />
          
          {/* Silently integrate the RevenueDashboard to establish the data model */}
          <RevenueDashboard leaderboardData={leaderboardData} />
          
          <ScrollArea className="h-[400px] rounded-md pr-4" style={{ height: "400px" }}>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {isLoading ? (
                // Loading skeleton cards
                Array(10).fill(0).map((_, index) => (
                  <Card key={index} className="bg-slate-800 border-slate-700 shadow-xl hover:shadow-2xl transition-shadow animate-pulse">
                    <div className="p-0">
                      <div className="h-24 bg-slate-700"></div>
                    </div>
                  </Card>
                ))
              ) : leaderboardData.length > 0 ? (
                // Display loaded data
                leaderboardData.map(item => (
                  <LeaderboardCard key={item.id} item={item} filterType={filterType} />
                ))
              ) : (
                // No data after filtering
                <div className="col-span-5 text-center py-10 text-slate-400">
                  No data available. Please import data or change filters.
                </div>
              )}
            </div>
          </ScrollArea>
        </>
      )}
      
      {/* Import dialog */}
      <ImportDialog
        isOpen={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
        onFileSelect={handleFileSelect}
        onDialogDrop={handleDialogDrop}
        handleDialogDragEnter={handleDialogDragEnter}
        handleDialogDragOver={handleDialogDragOver}
        handleDialogDragLeave={handleDialogDragLeave}
        isDialogDragging={isDialogDragging}
        fileInputRef={fileInputRef}
      />
      
      {/* Error dialog */}
      <ErrorDialog
        isOpen={isErrorDialogOpen}
        onOpenChange={setIsErrorDialogOpen}
        errorMessage={errorMessage}
      />
      
      {/* Hidden file input */}
      <input 
        ref={fileInputRef} 
        type="file" 
        accept=".xlsx, .xls" 
        className="hidden" 
        onChange={handleFileChange} 
      />
    </div>
  );
};

export default LeaderboardTab;
